import React, { useState, useEffect, useCallback } from "react";
import {
  FaCalendarAlt,
  FaUsers,
  FaUserGraduate,
  FaCog,
  FaHome,
  FaChartBar,
  FaChartLine,
  FaSignOutAlt,
  FaClock,
  FaShieldAlt,
  FaExclamationTriangle,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaCalendarTimes,
  FaHistory,
  FaCheckCircle,
  FaUserCog,
  FaMoneyBillWave,
} from "react-icons/fa";
import AvailabilityManager from "./AvailabilityManager";
import AppointmentTracker from "./AppointmentTracker";
import StudentTracker from "./StudentTracker";
import IncompleteAppointments from "./IncompleteAppointments";
import CompletedAppointments from "./CompletedAppointments";
import FomoSettings from "./FomoSettings";
import AdminActivityLogs from "./AdminActivityLogs";
import AnalyticsDashboard from "./AnalyticsDashboard";
import TrafficSources from "./TrafficSources";
import UserManagement from "./UserManagement";
import AccountingSection from "./AccountingSection";
import {
  logoutAdmin,
  getCurrentUser,
  getMailingList,
  getMailingListStats,
  removeEmailFromMailingList,
  updateEmailStatus,
} from "../firebase";
import {
  isAdminSessionValid,
  getAdminSession,
  getCurrentUserRole,
  getCurrentUserInfo,
  clearAllAdminData,
  startInactivityTimer,
  resetInactivityTimer,
  clearInactivityTimer,
  logAdminActivity,
} from "../utils/adminSecurity";
import {
  hasPermission,
  canAccessTab,
  getRoleDisplayName,
  PERMISSIONS,
} from "../utils/rolePermissions";
import "./AdminPanel.css";

// Mailing List Component
const MailingList = ({ userRole }) => {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonFormat, setJsonFormat] = useState("simple");

  // Check permissions
  const canManageMailingList = hasPermission(userRole, PERMISSIONS.MANAGE_MAILING_LIST);
  const canExportMailingList = hasPermission(userRole, PERMISSIONS.EXPORT_MAILING_LIST);

  useEffect(() => {
    fetchMailingData();
  }, []);

  const fetchMailingData = async () => {
    try {
      const [emailsData, statsData] = await Promise.all([
        getMailingList(),
        getMailingListStats(),
      ]);
      setEmails(emailsData);
      setStats(statsData);
    } catch (error) {
      console.error("Mailing list yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmail = async (emailId) => {
    if (
      window.confirm(
        "Bu e-posta adresini mailing list'ten kaldırmak istediğinizden emin misiniz?"
      )
    ) {
      try {
        const emailData = emails.find((e) => e.id === emailId);

        await removeEmailFromMailingList(emailId);
        fetchMailingData(); // Refresh data

        // Log mailing list modification
        logAdminActivity("mailing_list_modification", {
          action: "remove_email",
          emailId,
          email: emailData?.email,
          previousStatus: emailData?.status,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("E-posta kaldırılırken hata:", error);
        alert("E-posta kaldırılırken hata oluştu.");
      }
    }
  };

  const handleToggleStatus = async (emailId, currentStatus) => {
    try {
      const emailData = emails.find((e) => e.id === emailId);
      const newStatus = currentStatus === "active" ? "unsubscribed" : "active";

      await updateEmailStatus(emailId, newStatus);
      fetchMailingData(); // Refresh data

      // Log mailing list modification
      logAdminActivity("mailing_list_modification", {
        action: "status_change",
        emailId,
        email: emailData?.email,
        oldStatus: currentStatus,
        newStatus: newStatus,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("E-posta durumu güncellenirken hata:", error);
      alert("E-posta durumu güncellenirken hata oluştu.");
    }
  };

  // JSON Export Functions
  const generateJsonData = (format) => {
    const activeEmails = emails.filter((email) => email.status === "active");

    switch (format) {
      case "simple":
        return activeEmails.map((email) => email.email);

      case "mailchimp":
        return activeEmails.map((email) => ({
          email_address: email.email,
          status: "subscribed",
          merge_fields: {
            FNAME: "",
            LNAME: "",
            SOURCE: email.source || "website",
          },
        }));

      case "sendinblue":
        return activeEmails.map((email) => ({
          email: email.email,
          attributes: {
            SOURCE: email.source || "website",
            SIGNUP_DATE: email.addedAt || new Date().toISOString(),
          },
        }));

      case "constant_contact":
        return activeEmails.map((email) => ({
          email_address: email.email,
          first_name: "",
          last_name: "",
          custom_fields: [
            {
              custom_field_id: "source",
              value: email.source || "website",
            },
          ],
        }));

      case "full":
      default:
        return activeEmails.map((email) => ({
          id: email.id,
          email: email.email,
          status: email.status,
          source: email.source,
          addedAt: email.addedAt,
          lastUpdated: email.lastUpdated,
        }));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("JSON verisi panoya kopyalandı!");

      // Log data export (copy to clipboard)
      const data = JSON.parse(text);
      logAdminActivity("data_export", {
        exportType: "mailing_list_clipboard",
        method: "copy_to_clipboard",
        recordCount: Array.isArray(data) ? data.length : 1,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("JSON verisi panoya kopyalandı!");

      // Log data export (copy to clipboard - fallback)
      try {
        const data = JSON.parse(text);
        logAdminActivity("data_export", {
          exportType: "mailing_list_clipboard",
          method: "copy_to_clipboard_fallback",
          recordCount: Array.isArray(data) ? data.length : 1,
          timestamp: new Date().toISOString(),
        });
      } catch (parseError) {
        // If parsing fails, still log the action
        logAdminActivity("data_export", {
          exportType: "mailing_list_clipboard",
          method: "copy_to_clipboard_fallback",
          recordCount: "unknown",
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Log data export
    logAdminActivity("data_export", {
      exportType: "mailing_list_json",
      filename: filename,
      recordCount: data.length,
      format: filename.includes("simple")
        ? "simple"
        : filename.includes("mailchimp")
        ? "mailchimp"
        : filename.includes("sendinblue")
        ? "sendinblue"
        : filename.includes("constant_contact")
        ? "constant_contact"
        : "full",
      timestamp: new Date().toISOString(),
    });
  };

  if (loading) {
    return <div className="loading">Mailing list yükleniyor...</div>;
  }

  return (
    <div className="mailing-list">
      <h2>Mailing List</h2>
      <p>Randevu alan öğrencilerin e-posta adresleri</p>

      <div className="email-stats">
        <div className="stat-item">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">Toplam E-posta</span>
        </div>
        <div className="stat-item active">
          <span className="stat-number">{stats.active}</span>
          <span className="stat-label">Aktif</span>
        </div>
        <div className="stat-item unsubscribed">
          <span className="stat-number">{stats.unsubscribed}</span>
          <span className="stat-label">Abonelik İptal</span>
        </div>
        <div className="stat-item export-actions">
          {canExportMailingList && (
            <button
              className="export-json-btn"
              onClick={() => setShowJsonModal(true)}
              title="JSON formatında dışa aktar"
            >
              📋 JSON Export
            </button>
          )}
        </div>
      </div>

      <div className="emails-container">
        {emails.length > 0 ? (
          <div className="emails-grid">
            {emails.map((emailData) => (
              <div
                key={emailData.id}
                className={`email-item ${emailData.status}`}
              >
                <FaEnvelope className="email-icon" />
                <div className="email-details">
                  <span className="email-address">{emailData.email}</span>
                  <span className="email-meta">
                    {emailData.source} • {emailData.status}
                  </span>
                </div>
                {canManageMailingList && (
                  <div className="email-actions">
                    <button
                      className="toggle-status-btn"
                      onClick={() =>
                        handleToggleStatus(emailData.id, emailData.status)
                      }
                      title={
                        emailData.status === "active"
                          ? "Aboneliği iptal et"
                          : "Aktif hale getir"
                      }
                    >
                      {emailData.status === "active" ? "🔕" : "🔔"}
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveEmail(emailData.id)}
                      title="Kaldır"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-emails">
            <FaEnvelope size={48} />
            <p>Henüz kayıtlı e-posta adresi bulunmuyor.</p>
          </div>
        )}
      </div>

      {/* JSON Export Modal */}
      {showJsonModal && (
        <div
          className="json-modal-overlay"
          onClick={() => setShowJsonModal(false)}
        >
          <div className="json-modal" onClick={(e) => e.stopPropagation()}>
            <div className="json-modal-header">
              <h3>📋 JSON Export</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowJsonModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="json-modal-content">
              <div className="format-selector">
                <label>Export Formatı:</label>
                <select
                  value={jsonFormat}
                  onChange={(e) => setJsonFormat(e.target.value)}
                  className="format-select"
                >
                  <option value="simple">
                    Basit Liste (Sadece E-postalar)
                  </option>
                  <option value="mailchimp">Mailchimp Formatı</option>
                  <option value="sendinblue">Sendinblue/Brevo Formatı</option>
                  <option value="constant_contact">
                    Constant Contact Formatı
                  </option>
                  <option value="full">Tam Veri (Tüm Alanlar)</option>
                </select>
              </div>

              <div className="json-preview">
                <label>
                  Önizleme ({emails.filter((e) => e.status === "active").length}{" "}
                  aktif e-posta):
                </label>
                <pre className="json-content">
                  {JSON.stringify(
                    generateJsonData(jsonFormat).slice(0, 3),
                    null,
                    2
                  )}
                  {generateJsonData(jsonFormat).length > 3 &&
                    "\n... ve " +
                      (generateJsonData(jsonFormat).length - 3) +
                      " tane daha"}
                </pre>
              </div>

              <div className="json-actions">
                <button
                  className="copy-json-btn"
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify(generateJsonData(jsonFormat), null, 2)
                    )
                  }
                >
                  📋 Panoya Kopyala
                </button>
                <button
                  className="download-json-btn"
                  onClick={() =>
                    downloadJson(
                      generateJsonData(jsonFormat),
                      `mailing-list-${jsonFormat}-${
                        new Date().toISOString().split("T")[0]
                      }.json`
                    )
                  }
                >
                  💾 İndir
                </button>
              </div>

              <div className="format-info">
                <h4>Format Açıklamaları:</h4>
                <ul>
                  <li>
                    <strong>Basit Liste:</strong> Sadece e-posta adreslerinin
                    listesi
                  </li>
                  <li>
                    <strong>Mailchimp:</strong> Mailchimp API'si için uygun
                    format
                  </li>
                  <li>
                    <strong>Sendinblue/Brevo:</strong> Sendinblue/Brevo API
                    formatı
                  </li>
                  <li>
                    <strong>Constant Contact:</strong> Constant Contact API
                    formatı
                  </li>
                  <li>
                    <strong>Tam Veri:</strong> Tüm kayıtlı bilgiler dahil
                  </li>
                </ul>
                <p className="note">
                  <strong>Not:</strong> Sadece aktif (abonelik iptal edilmemiş)
                  e-postalar export edilir.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPanel = ({ onBackToHome, onLogout }) => {
  const [activeTab, setActiveTab] = useState("availability");
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Session timeout handler
  const handleSessionTimeout = useCallback(() => {
    logAdminActivity("session_timeout", {
      activeTab,
      timestamp: new Date().toISOString(),
    });

    alert(
      "Oturum süresi doldu. Güvenlik nedeniyle otomatik olarak çıkış yapılıyor."
    );
    handleLogout(true); // true = forced logout
  }, [activeTab]);

  // Activity handler to reset inactivity timer
  const handleUserActivity = useCallback(() => {
    if (isAdminSessionValid()) {
      resetInactivityTimer(handleSessionTimeout);
    }
  }, [handleSessionTimeout]);

  // Initialize session and user data
  useEffect(() => {
    // Check if session is valid
    if (!isAdminSessionValid()) {
      logAdminActivity("invalid_session_access", {
        timestamp: new Date().toISOString(),
      });
      onLogout();
      return;
    }

    const session = getAdminSession();
    const user = getCurrentUser();
    const role = getCurrentUserRole();

    if (session && user) {
      setCurrentUser(user);
      setUserRole(role);

      // Set first available tab based on user role
      const availableTabs = tabs.filter(tab => canAccessTab(role, tab.id));
      if (availableTabs.length > 0 && !canAccessTab(role, activeTab)) {
        setActiveTab(availableTabs[0].id);
      }

      // Panel access logging removed - too spammy
      // Only log login/logout and critical actions

      // Start inactivity timer
      startInactivityTimer(handleSessionTimeout);
    } else {
      onLogout();
    }

    return () => {
      clearInactivityTimer();
    };
  }, [handleSessionTimeout, onLogout]);

  // Session timer effect
  useEffect(() => {
    const updateSessionTimer = () => {
      const session = getAdminSession();
      if (session) {
        const timeLeft = Math.max(0, session.expiresAt - Date.now());
        const minutesLeft = Math.floor(timeLeft / (1000 * 60));

        setSessionTimeLeft(minutesLeft);

        // Show warning when 5 minutes or less remaining
        if (minutesLeft <= 5 && minutesLeft > 0 && !showSessionWarning) {
          setShowSessionWarning(true);
        } else if (minutesLeft > 5) {
          setShowSessionWarning(false);
        }

        // Auto logout when session expires
        if (minutesLeft <= 0) {
          handleSessionTimeout();
        }
      }
    };

    updateSessionTimer();
    const interval = setInterval(updateSessionTimer, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [handleSessionTimeout, showSessionWarning]);

  // Add activity listeners
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  const handleLogout = async (forced = false) => {
    const confirmMessage = forced
      ? "Oturum süresi doldu. Çıkış yapılıyor..."
      : "Çıkış yapmak istediğinizden emin misiniz?";

    if (forced || window.confirm(confirmMessage)) {
      try {
        // Log logout activity
        logAdminActivity("admin_logout", {
          activeTab,
          timestamp: new Date().toISOString(),
          forced,
        });

        // Clear all admin security data
        clearAllAdminData();
        clearInactivityTimer();

        // Firebase logout
        await logoutAdmin();

        onLogout();
      } catch (error) {
        console.error("Logout error:", error);
        if (!forced) {
          alert("Çıkış yapılırken hata oluştu.");
        }
        // Force logout even if Firebase logout fails
        clearAllAdminData();
        onLogout();
      }
    }
  };

  // Handle tab changes (logging removed - too frequent)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    handleUserActivity(); // Reset inactivity timer
  };

  // Extend session handler
  const handleExtendSession = () => {
    handleUserActivity();
    setShowSessionWarning(false);

    logAdminActivity("session_extended", {
      timestamp: new Date().toISOString(),
    });
  };

  const tabs = [
    {
      id: "availability",
      name: "Uygunluk Yönetimi",
      icon: FaCalendarAlt,
      description: "Randevu takvimini ve uygun saatleri yönetin",
    },
    {
      id: "appointments",
      name: "Randevu Takibi",
      icon: FaUsers,
      description: "Mevcut randevuları görüntüleyin ve yönetin",
    },
    {
      id: "incomplete",
      name: "Yarım Kalan Randevular",
      icon: FaCalendarTimes,
      description: "Form doldurup tarih seçmeyen kullanıcıları görüntüleyin",
    },
    {
      id: "completed",
      name: "Tamamlanan Randevular",
      icon: FaCheckCircle,
      description: "Tamamlanan randevuları ve detaylarını görüntüleyin",
    },
    {
      id: "students",
      name: "Öğrenci Takibi",
      icon: FaUserGraduate,
      description: "Öğrenci bilgilerini ve başvuru süreçlerini takip edin",
    },
    {
      id: "mailing",
      name: "Mailing List",
      icon: FaEnvelope,
      description: "Randevu alan öğrencilerin e-posta adreslerini yönetin",
    },
    {
      id: "activity",
      name: "Admin Aktiviteleri",
      icon: FaHistory,
      description:
        "Admin kullanıcılarının yaptığı kritik işlemleri görüntüleyin",
    },
    {
      id: "fomo",
      name: "FOMO Ayarları",
      icon: FaChartBar,
      description: "Sahte doluluk ve FOMO etkisi ayarlarını yönetin",
    },
    {
      id: "accounting",
      name: "Muhasebe Defteri",
      icon: FaMoneyBillWave,
      description: "Öğrenci ödemelerini ve taksitleri takip edin",
    },
    {
      id: "analytics",
      name: "Kullanıcı Analitikleri",
      icon: FaChartLine,
      description: "Kullanıcı davranışları ve marketing metriklerini görüntüleyin",
    },
    {
      id: "traffic",
      name: "Trafik Kaynakları",
      icon: FaChartBar,
      description: "Marketing kampanya performansı ve trafik kaynaklarını analiz edin",
    },
    {
      id: "users",
      name: "Kullanıcı Yönetimi",
      icon: FaUserCog,
      description: "Admin kullanıcılarını ve rollerini yönetin",
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "availability":
        return <AvailabilityManager />;
      case "appointments":
        return <AppointmentTracker />;
      case "incomplete":
        return <IncompleteAppointments />;
      case "completed":
        return <CompletedAppointments />;
      case "students":
        return <StudentTracker />;
      case "mailing":
        return <MailingList userRole={userRole} />;
      case "activity":
        return <AdminActivityLogs />;
      case "fomo":
        return <FomoSettings userRole={userRole} />;
      case "accounting":
        return <AccountingSection />;
      case "analytics":
        return <AnalyticsDashboard userRole={userRole} />;
      case "traffic":
        return <TrafficSources userRole={userRole} />;
      case "users":
        return <UserManagement userRole={userRole} />;
      default:
        return <AvailabilityManager />;
    }
  };

  return (
    <div className="admin-panel">
      {/* Session Warning */}
      {showSessionWarning && (
        <div className="session-warning">
          <div className="session-warning-content">
            <FaExclamationTriangle className="warning-icon" />
            <div className="warning-text">
              <h4>Oturum Süresi Uyarısı</h4>
              <p>Oturumunuz {sessionTimeLeft} dakika içinde sona erecek.</p>
            </div>
            <div className="warning-actions">
              <button
                className="extend-session-btn"
                onClick={handleExtendSession}
              >
                <FaClock /> Oturumu Uzat
              </button>
              <button className="logout-btn" onClick={() => handleLogout()}>
                <FaSignOutAlt /> Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <FaCog className="logo-icon" />
            <span className="logo-text">Admin Panel</span>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <FaTimes />
          </button>
        </div>

        <div className="sidebar-user">
          {currentUser && (
            <>
              <div className="user-avatar">
                <FaShieldAlt />
              </div>
              <div className="user-details">
                <span className="user-name">{currentUser.email}</span>
                <span className="user-role">{getRoleDisplayName(userRole)}</span>
                <span className="session-time">
                  {sessionTimeLeft !== null
                    ? `${sessionTimeLeft} dk kaldı`
                    : "Yükleniyor..."}
                </span>
              </div>
            </>
          )}
        </div>

        <nav className="sidebar-nav">
          {tabs
            .filter(tab => canAccessTab(userRole, tab.id))
            .map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => {
                    handleTabChange(tab.id);
                    setSidebarOpen(false);
                  }}
                >
                  <IconComponent className="nav-icon" />
                  <span className="nav-text">{tab.name}</span>
                </button>
              );
            })}
        </nav>

        <div className="sidebar-footer">
          <button className="footer-btn" onClick={onBackToHome}>
            <FaHome />
            <span>Ana Sayfa</span>
          </button>
          <button className="footer-btn logout" onClick={() => handleLogout()}>
            <FaSignOutAlt />
            <span>Çıkış Yap</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
            <FaBars />
          </button>
          <div className="page-title">
            {tabs.find((tab) => tab.id === activeTab)?.name || "Admin Panel"}
          </div>
        </div>

        {/* Content Area */}
        <div className="content-area">{renderTabContent()}</div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminPanel;
